import { Button } from '@ophelos/ui/components/button';
import { Slider } from '@ophelos/ui/components/slider';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface PaymentCountPickerProps {
  count: number;
  onChange: (count: number) => void;
  min: number;
  max: number;
  disabled?: boolean;
  /**
   * Translation key for the heading. Defaults to the plan tabs' wording, where the customer is
   * choosing a length for the first time; the budget step overrides it, because there they
   * already have a plan and are adjusting it.
   */
  headingKey?: string;
}

/**
 * How many payments a plan is spread over: the stepper, the figure, and the slider.
 *
 * Lifted out of `PaymentPlanTab` so the budget-assessment plan step is the same control rather
 * than a lookalike. `min` is a parameter because the two callers bound it differently — the plan
 * tabs floor at 2, the budget step floors at whatever the customer's disposable income affords.
 */
export function PaymentCountPicker({
  count,
  onChange,
  min,
  max,
  disabled = false,
  headingKey = 'paymentPlan.selectNumberOfPayments',
}: PaymentCountPickerProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-sm font-medium text-muted-foreground mb-2">{t(headingKey)}</p>
        <div className="flex w-1/2 mx-auto justify-between items-center py-2">
          <Button
            variant="subtle"
            size="icon"
            onClick={() => onChange(Math.max(count - 1, min))}
            className="rounded-full"
            disabled={disabled || count <= min}
            aria-label={t('paymentPlan.decreasePaymentCount')}
          >
            <Minus className="size-4" />
          </Button>
          <div className="text-6xl font-bold text-accent-foreground tabular-nums">{count}</div>
          <Button
            variant="subtle"
            size="icon"
            onClick={() => onChange(Math.min(count + 1, max))}
            className="rounded-full"
            disabled={disabled || count >= max}
            aria-label={t('paymentPlan.increasePaymentCount')}
          >
            <Plus className="size-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{t('paymentPlan.payments')}</p>
      </div>
      {/* The thumb carries `role="slider"` and `packages/ui`'s wrapper spreads props onto the
          Root, so an `aria-label` here would not reach it — naming the group is the part that
          can be done without changing a component `apps/hub` also renders. Announces the
          purpose on entry; naming the thumb itself needs that shared component to forward it. */}
      <div className="px-4" role="group" aria-label={t(headingKey)}>
        <Slider
          min={min}
          max={max}
          step={1}
          value={[count]}
          onValueChange={(value) => onChange(value[0])}
          className="w-full"
          disabled={disabled || min >= max}
        />
        {/* Bare numbers: the caption above the stepper already says what is being counted, and
            repeating "payments" on both ends said it three times in one glance. */}
        <div className="flex justify-between text-xs text-muted-foreground mt-2 tabular-nums">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  );
}
